import { useState } from 'react';
import { Trash2, Download, Tag, Loader2, Crown } from 'lucide-react';
import type { Category } from '@sharecost/shared';
import { useTranslation } from 'react-i18next';

interface ExpenseBatchBarProps {
  selectedCount: number;
  totalCount: number;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onDelete: () => void;
  onExportCsv: () => void;
  onChangeCategory: (categoryId: string | null) => void;
  categories: Category[];
  isDeleting: boolean;
  isUpdating: boolean;
  premiumLocked?: boolean;
  onPremiumBlocked?: () => void;
}

export function ExpenseBatchBar({
  selectedCount,
  totalCount,
  onSelectAll,
  onClearSelection,
  onDelete,
  onExportCsv,
  onChangeCategory,
  categories,
  isDeleting,
  isUpdating,
  premiumLocked,
  onPremiumBlocked,
}: ExpenseBatchBarProps) {
  const { t } = useTranslation();
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);

  return (
    <div className="flex items-center justify-between rounded-lg bg-surface-secondary px-4 py-2.5">
      <div className="flex items-center gap-3">
        <button
          onClick={selectedCount > 0 ? onClearSelection : onSelectAll}
          className="text-sm font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300"
        >
          {selectedCount > 0 ? t('web:common.clearSelection') : t('expenses:importedTransactions.selectAll')}
        </button>
        <span className="text-sm text-text-tertiary">
          {selectedCount} of {totalCount} selected
        </span>
      </div>

      <div className="flex items-center gap-2">
        {/* Change category */}
        <div className="relative">
          <button
            onClick={() => setShowCategoryPicker(!showCategoryPicker)}
            disabled={isUpdating}
            className="flex items-center gap-1.5 rounded-lg border border-border-secondary px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-hover disabled:opacity-50 transition-colors"
          >
            {isUpdating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Tag className="h-4 w-4" />}
            {t('expenses:form.category.label')}
          </button>
          {showCategoryPicker && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowCategoryPicker(false)} />
              <div className="absolute right-0 top-full z-20 mt-1 max-h-60 w-48 overflow-y-auto rounded-lg border border-border-primary bg-surface-primary py-1 shadow-lg">
                <button
                  onClick={() => { onChangeCategory(null); setShowCategoryPicker(false); }}
                  className="w-full px-3 py-1.5 text-left text-sm text-text-secondary hover:bg-surface-hover"
                >
                  {t('web:common.noCategory')}
                </button>
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => { onChangeCategory(cat.id); setShowCategoryPicker(false); }}
                    className="w-full px-3 py-1.5 text-left text-sm text-text-secondary hover:bg-surface-hover"
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Export CSV */}
        <button
          onClick={premiumLocked ? onPremiumBlocked : onExportCsv}
          className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm transition-colors ${
            premiumLocked
              ? 'border-border-secondary text-text-quaternary cursor-not-allowed'
              : 'border-border-secondary text-text-secondary hover:bg-surface-hover'
          }`}
        >
          {premiumLocked ? <Crown className="h-4 w-4 text-amber-500" /> : <Download className="h-4 w-4" />}
          {t('web:expenseBatchBar.exportCsv')}
        </button>

        {/* Delete */}
        <button
          onClick={onDelete}
          disabled={isDeleting}
          className="flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
        >
          {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          {t('common:button.delete')}
        </button>
      </div>
    </div>
  );
}
